import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { reviewsApi, reviewsQueryKeys } from '../api';
import type { ReviewFilters, CreateReviewDto, UpdateReviewDto } from '../types';

// Hook for fetching reviews list
export function useReviews(filters?: ReviewFilters) {
  return useQuery({
    queryKey: reviewsQueryKeys.list(filters),
    queryFn: () => reviewsApi.getAll(filters),
  });
}

// Hook for fetching single review
export function useReview(id: number | string) {
  return useQuery({
    queryKey: reviewsQueryKeys.detail(id),
    queryFn: () => reviewsApi.getById(id),
    enabled: !!id,
  });
}

// Hook for creating review
export function useCreateReview() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateReviewDto) => reviewsApi.create(data),
    onSuccess: () => {
      toast.success(t('reviewCreatedSuccess'));
      queryClient.invalidateQueries({ queryKey: reviewsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingReview', { error: 'Unknown error' })));
    },
  });
}

// Hook for updating review
export function useUpdateReview() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateReviewDto }) =>
      reviewsApi.update({ id, data }),
    onSuccess: (_, variables) => {
      toast.success(t('reviewUpdatedSuccess'));
      queryClient.invalidateQueries({ queryKey: reviewsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: reviewsQueryKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingReview', { error: 'Unknown error' })));
    },
  });
}

// Hook for deleting review
export function useDeleteReview() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => reviewsApi.delete(id),
    onSuccess: () => {
      toast.success(t('reviewDeletedSuccess'));
      queryClient.invalidateQueries({ queryKey: reviewsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingReview', { error: 'Unknown error' })));
    },
  });
}
